import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { getBoardWorkingDate, getLocalDateString } from '../utils/timezone.ts';

/**
 * The app-wide WORKING DATE (local `YYYY-MM-DD`): the one day the Board, Manpower and Shifts screens all
 * operate on, so moving between them never silently changes the day you are looking at.
 *
 * It keeps the board's long-standing rule (see `getBoardWorkingDate`): a date you pick sticks across
 * refreshes for the rest of that real-world day, and the next day you start on today again. The choice is
 * persisted under the same `oc-board-selected-date` / `oc-board-selected-saved-on` keys the board always
 * used, so screens that only read it at mount (Simulate) still agree.
 */

type WorkingDateContextValue = readonly [string, (date: string) => void];

const WorkingDateContext = createContext<WorkingDateContextValue | undefined>(undefined);

export const persistWorkingDate = (date: string): void => {
  try {
    localStorage.setItem('oc-board-selected-date', date);
    localStorage.setItem('oc-board-selected-saved-on', getLocalDateString());
  } catch { /* localStorage unavailable */ }
};

export const WorkingDateProvider: React.FC<{ children: React.ReactNode; initialDate?: string }> = ({ children, initialDate }) => {
  const [date, setDateState] = useState<string>(() => initialDate ?? getBoardWorkingDate());

  const setDate = useCallback((next: string) => {
    // An emptied <input type="date"> reports ''; keep the last real day rather than blanking every screen.
    if (!next) return;
    setDateState(next);
    persistWorkingDate(next);
  }, []);

  const value = useMemo(() => [date, setDate] as const, [date, setDate]);
  return <WorkingDateContext.Provider value={value}>{children}</WorkingDateContext.Provider>;
};

/**
 * `[date, setDate]` for the shared working date. Outside a provider (an isolated component test) it falls
 * back to a private, non-shared date so the component still works on its own.
 */
export const useWorkingDate = (): WorkingDateContextValue => {
  const ctx = useContext(WorkingDateContext);
  const [fallback, setFallback] = useState<string>(getBoardWorkingDate);
  const setFallbackDate = useCallback((next: string) => { if (next) setFallback(next); }, []);
  return ctx ?? ([fallback, setFallbackDate] as const);
};
